import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, ChevronRightIcon, MinusIcon, PackageIcon, PlusIcon, TruckIcon } from "lucide-react";
import { ProductCard } from "../components/ProductCard";
import { ProductDetails } from "../components/product/ProductDetails";
import { useCart } from "../contexts/CartContext";
import { products } from "../data/products";
import { collections } from "../data/collections";
import { formatPrice } from "../utils/currency";
export function ProductPage() {
  const {
    slug
  } = useParams();
  const product = products.find(p => p.slug === slug);
  const {
    addItem
  } = useCart();
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  useEffect(() => {
    setActiveImage(0);
    setQty(1);
  }, [slug]);
  if (!product) {
    return <div className="mx-auto max-w-7xl px-6 py-32 text-center" data-id="element-389" data-mp="1lbfxtqk0j1fm">
        <h1 className="font-display text-3xl font-bold" data-id="element-390" data-mp="1cofd77uvcr1w">Product not found</h1>
        <p className="mt-3 text-muted" data-id="element-391" data-mp="1cqi19mcgoa3z">This product may no longer be available.</p>
        <Link to="/shop" className="mt-8 inline-block rounded-full bg-brand px-6 py-3 text-sm font-bold text-night" data-id="element-392" data-mp="1gnxord0svps2">
          Back to shop
        </Link>
      </div>;
  }
  const collection = collections.find(c => c.slug === product.collection);
  const sameCategory = products.filter(p => p.slug !== product.slug && p.category === product.category);
  const related = (sameCategory.length >= 4 ? sameCategory : products.filter(p => p.slug !== product.slug)).slice(0, 4);
  return <div className="bg-night" data-id="element-393" data-mp="12x17gicbqv0b">
      <div className="mx-auto max-w-7xl px-6 pt-6" data-id="element-394" data-mp="17p2hdxm0s9n6">
        <nav aria-label="Breadcrumb" data-id="element-395" data-mp="17pua90o8ft9r">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-subtle" data-id="element-396" data-mp="17qvm37d2ojsj">
            <li data-id="element-397" data-mp="a7khrsg39s7s"><Link to="/" className="hover:text-white" data-id="element-398" data-mp="a7kg7o0fxk5o">Home</Link></li>
            <li aria-hidden="true" data-id="element-399" data-mp="f7gldqkvjhmt"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-400" data-mp="f6os4mer1vzp" /></li>
            <li data-id="element-401" data-mp="770m4q6qu6pr">
              <Link to={`/shop?collection=${product.collection}`} className="hover:text-white" data-id="element-402" data-mp="76iqnvrwnwiw">
                {collection?.name}
              </Link>
            </li>
            <li aria-hidden="true" data-id="element-403" data-mp="gy4ydi51bdgw"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-404" data-mp="gwk65z6706m7" /></li>
            <li className="text-white" aria-current="page" data-id="element-405" data-mp="bz26z0hz95re">{product.name}</li>
          </ol>
        </nav>
      </div>

      <section className="mx-auto grid max-w-7xl gap-10 px-6 py-8 lg:grid-cols-2 lg:gap-16 lg:py-12" data-id="element-406" data-mp="3kdtzkaa7209b">
        <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-407" data-mp="3gds2yfjbfx0w">
          <div className="relative overflow-hidden rounded-3xl bg-white" data-id="element-408" data-mp="3ucrupw24ks1a">
            <div className="aspect-square w-full" data-id="element-409" data-mp="3ue2znz7b45tz" />
            <AnimatePresence initial={false} data-id="element-410" data-mp="3pdf9ffqwjt0t">
              <motion.img key={product.images[activeImage]} src={product.images[activeImage]} alt={product.name} initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} className="absolute inset-0 h-full w-full object-contain p-4" data-id="element-411" data-mp="3pcdo8ig69q5d" />
            </AnimatePresence>
            {product.badge && <span className="absolute left-4 top-4 rounded-full bg-night px-3 py-1 text-xs font-bold text-brand" data-id="element-412" data-mp="2ij7mzoruwyau">
                {product.badge}
              </span>}
          </div>
          {product.images.length > 1 && <div className="mt-4 flex gap-3" data-id="element-413" data-mp="3a9dl9lcd4r7m">
              {product.images.map((img, i) => <button key={img} type="button" onClick={() => setActiveImage(i)} aria-label={`Show image ${i + 1}`} aria-pressed={activeImage === i} className={`overflow-hidden rounded-xl border-2 bg-white transition-colors ${activeImage === i ? "border-brand" : "border-transparent opacity-70 hover:opacity-100"}`} data-id="element-414" data-mp="2wih6axbd08j1" data-mpi={i}>
                  <img src={img} alt="" className="h-20 w-20 object-contain" data-id="element-415" data-mp="20dgygjbe5iwi" />
                </button>)}
            </div>}
        </div>

        <div data-id="element-416" data-mp="1rd22mq0swler">
          <p className="text-sm text-subtle" data-id="element-417" data-mp="1rdbu6i1hv0mk">
            {product.brand} · {product.format}
          </p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-id="element-418" data-mp="2o3uibmjtobg0">{product.name}</h1>
          <p className="mt-3 text-lg text-white/80" data-id="element-419" data-mp="2k2sip4lztxdi">{product.tagline}</p>
          <p className="mt-6 font-display text-3xl font-bold text-brand" data-id="element-420" data-mp="2farc9zbib0ve">{formatPrice(product.price)}</p>
          <p className="mt-1 text-xs text-subtle" data-id="element-421" data-mp="2acg0vk5kcib2">Incl. VAT · Free shipping over €50</p>

          <p className="mt-6 max-w-lg leading-relaxed text-muted" data-id="element-422" data-mp="37r1zfs4cnuxs">{product.description}</p>

          <ul className="mt-6 space-y-2.5" data-id="element-423" data-mp="330lm080j49bf">
            {product.benefits.map(b => <li key={b} className="flex items-start gap-3 text-sm" data-id="element-424" data-mp="32yqyclxvi33n">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-425" data-mp="3adb3jcntyuws" />
                {b}
              </li>)}
          </ul>

          <div className="mt-8 flex gap-3" data-id="element-426" data-mp="3k9ed33l0hvtr">
            <div className="flex items-center rounded-full border border-line" data-id="element-427" data-mp="3k8mk80isuc76">
              <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))} className="p-4 text-muted hover:text-white" aria-label="Decrease quantity" data-id="element-428" data-mp="3ocar0gzlx3rd">
                <MinusIcon className="h-4 w-4" data-id="element-429" data-mp="6v1beqjwvcle" />
              </button>
              <span className="w-8 text-center font-semibold" aria-live="polite" data-id="element-430" data-mp="6vt7e2hf7cc7">{qty}</span>
              <button type="button" onClick={() => setQty(q => q + 1)} className="p-4 text-muted hover:text-white" aria-label="Increase quantity" data-id="element-431" data-mp="btl1921ml8j1">
                <PlusIcon className="h-4 w-4" data-id="element-432" data-mp="ksg2s0tq5ug0" />
              </button>
            </div>
            <button type="button" onClick={() => {
            addItem(product.slug, qty);
            setQty(1);
          }} className="flex-1 rounded-full bg-brand px-8 py-4 text-sm font-bold text-night transition-colors hover:bg-brand-dark active:scale-[0.99]" data-id="element-433" data-mp="2gvuohg8wx41j">
              Add to cart — {formatPrice(product.price * qty)}
            </button>
          </div>

          <ul className="mt-6 grid gap-3 rounded-2xl border border-line bg-surface p-5 text-sm sm:grid-cols-2" data-id="element-434" data-mp="2vn3gtx4793id">
            <li className="flex items-center gap-3" data-id="element-435" data-mp="39mus3q3c05so">
              <PackageIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-436" data-mp="39kiaxf5edzgo" /> Discreet, unbranded packaging
            </li>
            <li className="flex items-center gap-3" data-id="element-437" data-mp="1f81xnue6pl2b">
              <TruckIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-438" data-mp="1f8tut9nfjqup" /> Ships from the Netherlands
            </li>
          </ul>

          <div className="mt-10" data-id="element-439" data-mp="3lwc2tdrd0oty">
            <ProductDetails product={product} data-id="element-440" data-mp="3ly5dr5909v7r" />
          </div>
        </div>
      </section>

      <section className="border-t border-line py-20" data-id="element-441" data-mp="3qulydpgzxrg3">
        <div className="mx-auto max-w-7xl px-6" data-id="element-442" data-mp="3d4il3o03h82s">
          <h2 className="font-display text-3xl font-bold tracking-tight" data-id="element-443" data-mp="3d3qu6qhfzyiu">You may also like</h2>
          <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-5 lg:grid-cols-4" data-id="element-444" data-mp="3h4sy3kmb0yr5">
            {related.map(p => <ProductCard key={p.slug} product={p} data-id="element-445" data-mp="338fikh1bz7fb" />)}
          </div>
        </div>
      </section>
    </div>;
}